import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, User, Calendar, BookOpen, TrendingUp, Target, FileText } from 'lucide-react';
import api from '../utils/api';

interface LessonFeedback {
  id: string;
  feedback: string;
  stepsToImprove: string;
  homeworkTargets: string;
  createdAt: string;
  booking: {
    startTime: string;
    endTime: string;
    subject: {
      name: string;
      category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
    };
    tutor: {
      name: string;
    };
  };
}

const FeedbackPage: React.FC = () => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<LessonFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchFeedbacks();
    }
  }, [user]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/feedback/student/${user?.id}`);
      setFeedbacks(response.data);
    } catch (error: any) {
      console.error('Failed to fetch feedbacks:', error);
      setError('Failed to load feedback. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London'
    });
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <MessageSquare className="w-5 h-5 text-red-600 mr-3" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if any feedback has blank fields
  const hasBlankFeedback = feedbacks.some(feedback => 
    !feedback.feedback || 
    !feedback.stepsToImprove || 
    !feedback.homeworkTargets ||
    feedback.feedback.trim() === '' ||
    feedback.stepsToImprove.trim() === '' ||
    feedback.homeworkTargets.trim() === ''
  );

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lesson Feedback</h1>
        <p className="text-gray-600">
          Review feedback from your tutor for each completed lesson
        </p>
      </div>

      {/* Show message if there's any blank feedback */}
      {hasBlankFeedback && feedbacks.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <MessageSquare className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <p className="text-yellow-800 font-medium">Some feedback is incomplete</p>
              <p className="text-yellow-700 text-sm mt-1">
                Please contact Ethan to have a short discussion about how the lesson went.
              </p>
            </div>
          </div>
        </div>
      )}

      {feedbacks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No feedback available yet</p>
          <p className="text-sm text-gray-500">
            Feedback will appear here after your tutor provides comments on your completed lessons
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {feedbacks.map((feedback) => {
            const isBlank = !feedback.feedback || 
              !feedback.stepsToImprove || 
              !feedback.homeworkTargets ||
              feedback.feedback.trim() === '' ||
              feedback.stepsToImprove.trim() === '' ||
              feedback.homeworkTargets.trim() === '';
            
            return (
              <div key={feedback.id} className={`bg-white rounded-lg shadow-sm border ${isBlank ? 'border-yellow-300' : 'border-gray-200'} p-6`}>
                {/* Show indicator if this feedback is blank */}
                {isBlank && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                    <p className="text-yellow-700 text-sm">
                      ℹ️ Feedback pending - please contact Ethan for a discussion about this lesson
                    </p>
                  </div>
                )}
                
                {/* Lesson Header */}
                <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feedback.booking.subject.name} - {formatSubjectCategory(feedback.booking.subject.category)}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 space-x-4">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{formatDate(feedback.booking.startTime)}</span>
                    </div>
                    <div className="flex items-center">
                      <span>{formatTime(feedback.booking.startTime)} - {formatTime(feedback.booking.endTime)}</span>
                    </div>
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      <span>with {feedback.booking.tutor.name}</span>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Feedback added: {formatDate(feedback.createdAt)}
                </div>
              </div>

              {/* Feedback Sections */}
              <div className="space-y-6">
                {/* General Feedback */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-2">General Feedback</h4>
                      <p className="text-blue-800 leading-relaxed">{feedback.feedback}</p>
                    </div>
                  </div>
                </div>

                {/* Steps to Improve */}
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                      <TrendingUp className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900 mb-2">Steps to Improve</h4>
                      <p className="text-orange-800 leading-relaxed">{feedback.stepsToImprove}</p>
                    </div>
                  </div>
                </div>

                {/* Homework Targets */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3 mt-1">
                      <Target className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-2">Homework Targets</h4>
                      <p className="text-green-800 leading-relaxed">{feedback.homeworkTargets}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;